import React from "react";
import { Link } from "react-router-dom";
import { formatJMD, isSoldOut, totalStock, salePriceCents } from "@/lib/format";
import { Countdown } from "@/components/Countdown";
import { usePromo } from "@/context/PromoContext";

export function ProductCard({ product, index = 0 }) {
  const soldOut = isSoldOut(product);
  const onBody = (product.gallery || []).find(Boolean);
  const promo = usePromo();
  const onSale = promo.active && promo.sale_percent > 0 && product.status !== "draft";
  const sale = onSale ? salePriceCents(product.price_cents, promo.sale_percent) : null;

  return (
    <Link
      to={`/product/${product.slug || product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group relative bg-card border border-border hover:border-jamaica-gold transition-colors duration-300 flex flex-col animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="relative aspect-square overflow-hidden bg-zinc-900">
        {/* front-of-shirt (default) */}
        <img
          src={product.flagship_image}
          alt={product.title}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${onBody ? "group-hover:opacity-0" : ""} ${soldOut ? "opacity-40 grayscale" : ""}`}
        />
        {/* on-body / lifestyle swap on hover */}
        {onBody && (
          <img
            src={onBody}
            alt={`${product.title} on body`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-105 ${soldOut ? "opacity-0 grayscale" : ""}`}
          />
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-10">
          {product.limited_drop && (
            <span className="bg-red-600 text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5">
              Limited Drop
            </span>
          )}
          {product.best_seller && !product.limited_drop && (
            <span className="bg-jamaica-gold text-black font-mono text-[10px] uppercase tracking-widest px-2 py-0.5">
              Best Seller
            </span>
          )}
        </div>
        {onSale && !soldOut && (
          <div className="absolute top-2 right-2 z-10">
            <span data-testid={`sale-badge-${product.id}`} className="bg-jamaica-green text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5">
              {promo.sale_percent}% Off
            </span>
          </div>
        )}
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <span className="bg-zinc-800 text-zinc-200 font-display text-lg tracking-widest px-4 py-1 -rotate-6 border border-zinc-600">
              SOLD OUT
            </span>
          </div>
        )}
        {product.limited_drop && product.drop_ends_at && !soldOut && (
          <div className="absolute bottom-2 left-2 z-10">
            <Countdown endsAt={product.drop_ends_at} />
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider bg-secondary text-muted-foreground border border-border px-1.5 py-0.5">
            {product.fit}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{product.color}</span>
        </div>
        <h3 className="font-heading font-bold uppercase text-sm leading-tight group-hover:text-jamaica-gold transition-colors">
          {product.title}
        </h3>
        {onSale ? (
          <span className="flex items-baseline gap-2 mt-1">
            <span data-testid={`sale-price-${product.id}`} className="font-mono text-jamaica-gold text-sm">{formatJMD(sale)}</span>
            <span className="font-mono text-zinc-500 text-xs line-through">{formatJMD(product.price_cents)}</span>
          </span>
        ) : (
          <span className="font-mono text-jamaica-gold text-sm mt-1">{formatJMD(product.price_cents)}</span>
        )}
      </div>
    </Link>
  );
}
